/**
 * Created by neo on 10.01.2026
 * Challenge Detail Screen - Shows challenge details, map, locations, and leaderboard
 * Modernized: Uses react-query hooks instead of manual loading states and static API calls
 */
import * as React from 'react';
import { observer } from 'mobx-react';
import { useMemo, useCallback, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Container, Row, Col } from 'reactstrap';
import { QRCodeCanvas } from 'qrcode.react';
import styled from '@emotion/styled';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { useTrans } from '../../Store/System/LocalizationStore';
import { useToast } from '../../Hooks/useToast';
import { useLocalized } from '../../Hooks/useLocalized';
import { useLocalizedImage } from '../../Hooks/useLocalizedMedia';
import { useLanguage } from '../../Hooks/useLanguage';
import {
  useRouteChallenge,
  useIndividualChallenge,
  useIndividualChallengeParticipation,
  useChallengeRanking,
  useMyRouteChallengeTeam,
  useTeamRanking,
  useJoinIndividualChallenge,
  useLeaveIndividualChallenge,
} from '../../Hooks/useChallengeQueries';
import { RouteChallengeTeam } from '../../Model/Engagement/RouteChallenge/RouteChallengeTeam';
import { Theme } from '../../Theme/Theme';
import { ChallengeHeader } from './Components/ChallengeHeader';
import { ChallengeStats } from './Components/ChallengeStats';
import { ChallengeLocations } from './Components/ChallengeLocations';
import { ChallengeLeaderboard } from './Components/ChallengeLeaderboard';
import { ChallengeMyProgress } from './Components/ChallengeMyProgress';
import { ChallengeMap } from './Components/ChallengeMap';
import { ChallengeLocationModal } from './Components/ChallengeLocationModal';
import { RouteChallengeLocation } from '../../Model/Engagement/RouteChallenge/RouteChallengeLocation';
import { CreateTeamModal } from './Components/CreateTeamModal';
import { TeamBrowseModal } from './Components/TeamBrowseModal';
import { Button } from '../../Components/Button';
import { ChallengeDetailSkeleton } from './ChallengeDetailSkeleton';

dayjs.extend(relativeTime);

const PageContainer = styled(Container)`
  padding-top: 24px;
  padding-bottom: 48px;
`;

const ContentRow = styled(Row)`
  margin-top: 32px;
`;

const MainContent = styled(Col)`
  margin-bottom: 24px;
`;

const SidebarContent = styled(Col)`
  @media (min-width: 992px) {
    position: sticky;
    top: 80px;
    align-self: flex-start;
  }
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 24px;
  text-align: center;
`;

const ErrorEmoji = styled.span`
  font-size: 64px;
  margin-bottom: 24px;
`;

const ErrorTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  margin-bottom: 12px;
  color: ${Theme.Colors.textColor};
`;

const ErrorDescription = styled.p`
  font-size: 16px;
  color: ${Theme.Colors.charcoal};
  max-width: 400px;
`;

const BackButton = styled.button`
  margin-top: 24px;
  padding: 12px 32px;
  background-color: ${Theme.Colors.primary};
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: ${Theme.Colors.primaryDark};
  }
`;

const AppBanner = styled.div`
  background: linear-gradient(135deg, ${Theme.Colors.primary} 0%, ${Theme.Colors.primaryDark} 100%);
  border-radius: 16px;
  padding: 24px;
  margin-top: 24px;
  margin-bottom: 32px;
  display: flex;
  align-items: center;
  gap: 24px;
  color: white;

  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
  }
`;

const AppBannerContent = styled.div`
  flex: 1;
`;

const AppBannerTitle = styled.h3`
  font-size: 18px;
  font-weight: 700;
  margin: 0 0 8px 0;
`;

const AppBannerDescription = styled.p`
  font-size: 14px;
  margin: 0;
  opacity: 0.9;
  line-height: 1.5;
`;

const AppBannerQRContainer = styled.div`
  background: white;
  border-radius: 12px;
  padding: 8px;
  flex-shrink: 0;
`;

const AppStoreLinks = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 16px;

  @media (max-width: 768px) {
    justify-content: center;
  }
`;

const AppStoreLink = styled.a`
  display: inline-block;
  height: 40px;

  img {
    height: 100%;
    width: auto;
  }
`;

const ActionCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 24px;
  margin-top: 24px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  display: flex;
  align-items: center;
  gap: 16px;
  justify-content: space-between;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const ActionContent = styled.div`
  flex: 1;
`;

const ActionTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: ${Theme.Colors.textColor};
  margin: 0 0 4px 0;
`;

const ActionDescription = styled.p`
  font-size: 14px;
  color: ${Theme.Colors.charcoal};
  margin: 0;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 12px;
  flex-shrink: 0;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

export type ChallengeType = 'route' | 'individual';

export type ChallengeDetailScreenProps = {};

export const ChallengeDetailScreen: React.FC<ChallengeDetailScreenProps> = observer(() => {
  const { t } = useTrans();
  const { language } = useLanguage();
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const { challengeId } = useParams<{ challengeId: string }>();

  const appDeepLink = useMemo(() => {
    const fullUrl = `https://coach.kinastic.com${location.pathname}`;
    return `https://open.kinastic.com/?link=${encodeURIComponent(
      fullUrl,
    )}&apn=app.flinq.athlete&ibi=app.flinq.athlete&isi=1465156373&efr=1`;
  }, [location.pathname]);

  const { data: routeChallenge, isLoading: isLoadingRoute } = useRouteChallenge(challengeId);
  const { data: individualChallenge, isLoading: isLoadingIndividual } = useIndividualChallenge(challengeId);

  const challengeType = useMemo<ChallengeType | null>(() => {
    if (individualChallenge) return 'individual';
    if (routeChallenge) return 'route';
    return null;
  }, [individualChallenge, routeChallenge]);

  const challenge = individualChallenge || routeChallenge;

  const { data: myParticipation } = useIndividualChallengeParticipation(
    challengeType === 'individual' ? challengeId : undefined,
  );
  const { data: participants = [] } = useChallengeRanking(
    challengeType === 'individual' ? challengeId : undefined,
    { page: 0, size: 20 },
  );

  const { data: myTeam, refetch: refetchMyTeam } = useMyRouteChallengeTeam(
    challengeType === 'route' ? challengeId : undefined,
  );
  const { data: teams = [], refetch: refetchTeamRanking } = useTeamRanking(
    challengeType === 'route' ? challengeId : undefined,
    { page: 0, size: 20 },
  );

  const joinMutation = useJoinIndividualChallenge();
  const leaveMutation = useLeaveIndividualChallenge();

  // These hooks MUST be called before any early returns (Rules of Hooks)
  const challengeName = useLocalized(challenge?.name);
  const challengeDescription = useLocalized(challenge?.description);
  const challengeImage = useLocalizedImage(challenge?.images);

  const [selectedLocation, setSelectedLocation] = useState<RouteChallengeLocation | null>(null);
  const [selectedLocationIndex, setSelectedLocationIndex] = useState<number>(0);
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [createTeamModalOpen, setCreateTeamModalOpen] = useState(false);
  const [browseTeamsModalOpen, setBrowseTeamsModalOpen] = useState(false);
  const [isLeavingTeam, setIsLeavingTeam] = useState(false);

  const handleGoBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const handleLocationClick = useCallback(
    (clickedLocation: RouteChallengeLocation, index: number) => {
      navigate(`/motivation/challenge/${challengeId}/location/${clickedLocation.id}`);
    },
    [navigate, challengeId],
  );

  const handleCloseLocationModal = useCallback(() => {
    setLocationModalOpen(false);
  }, []);

  const handleJoinIndividual = useCallback(async () => {
    if (!challengeId) return;
    try {
      await joinMutation.mutateAsync(challengeId);
      toast.showToast(
        t('challenge.join.success', { defaultValue: 'Successfully joined challenge!' }),
        'success',
      );
    } catch (error) {
      console.error('Error joining challenge:', error);
      toast.showToast(
        t('challenge.join.error', { defaultValue: 'Failed to join challenge. Please try again.' }),
        'error',
      );
    }
  }, [challengeId, joinMutation, toast, t]);

  const handleLeaveIndividual = useCallback(async () => {
    if (!challengeId) return;
    try {
      await leaveMutation.mutateAsync(challengeId);
      toast.showToast(
        t('challenge.leave.success', { defaultValue: 'Successfully left challenge!' }),
        'success',
      );
    } catch (error) {
      console.error('Error leaving challenge:', error);
      toast.showToast(
        t('challenge.leave.error', { defaultValue: 'Failed to leave challenge. Please try again.' }),
        'error',
      );
    }
  }, [challengeId, leaveMutation, toast, t]);

  const handleCreateTeam = useCallback(() => {
    setCreateTeamModalOpen(true);
  }, []);

  const handleBrowseTeams = useCallback(() => {
    setBrowseTeamsModalOpen(true);
  }, []);

  const handleTeamCreated = useCallback(
    async (team: RouteChallengeTeam) => {
      setCreateTeamModalOpen(false);
      await Promise.all([refetchMyTeam(), refetchTeamRanking()]);
    },
    [refetchMyTeam, refetchTeamRanking],
  );

  const handleTeamJoined = useCallback(
    async (team: RouteChallengeTeam) => {
      setBrowseTeamsModalOpen(false);
      await Promise.all([refetchMyTeam(), refetchTeamRanking()]);
    },
    [refetchMyTeam, refetchTeamRanking],
  );

  const handleLeaveTeam = useCallback(async () => {
    if (!myTeam || isLeavingTeam) return;
    setIsLeavingTeam(true);
    try {
      await myTeam.leave();
      toast.showToast(
        t('team.leave.success', { defaultValue: 'Successfully left team!' }),
        'success',
      );
      await Promise.all([refetchMyTeam(), refetchTeamRanking()]);
    } catch (error) {
      console.error('Error leaving team:', error);
      toast.showToast(
        t('team.leave.error', { defaultValue: 'Failed to leave team. Please try again.' }),
        'error',
      );
    } finally {
      setIsLeavingTeam(false);
    }
  }, [myTeam, isLeavingTeam, toast, t, refetchMyTeam, refetchTeamRanking]);

  if (isLoadingRoute || isLoadingIndividual) {
    return <ChallengeDetailSkeleton />;
  }

  if (!challenge || !challengeId) {
    return (
      <PageContainer>
        <ErrorContainer>
          <ErrorEmoji>🔍</ErrorEmoji>
          <ErrorTitle>{t('challenge.notFound', { defaultValue: 'Challenge Not Found' })}</ErrorTitle>
          <ErrorDescription>
            {t('challenge.notFoundDescription', {
              defaultValue: 'This challenge could not be found. It may have been removed or the link is incorrect.',
            })}
          </ErrorDescription>
          <BackButton onClick={handleGoBack}>
            {t('common.goBack', { defaultValue: 'Go Back' })}
          </BackButton>
        </ErrorContainer>
      </PageContainer>
    );
  }

  const locations = challenge.locations;

  return (
    <PageContainer>
      <ChallengeHeader
        challenge={challenge}
        challengeType={challengeType!}
        challengeName={challengeName}
        challengeDescription={challengeDescription}
        challengeImage={challengeImage}
        onBack={handleGoBack}
      />

      <AppBanner>
        <AppBannerContent>
          <AppBannerTitle>
            📱 {t('challenge.appBanner.title', { defaultValue: 'Get the Full Experience' })}
          </AppBannerTitle>
          <AppBannerDescription>
            {t('challenge.appBanner.description', {
              defaultValue:
                'For the best challenge experience with live tracking, activity sync, and real-time progress updates, use the KINASTIC app on your phone.',
            })}
          </AppBannerDescription>
          <AppStoreLinks>
            <AppStoreLink
              href="https://apps.apple.com/app/kinastic-coach/id1465156373"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img
                src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg"
                alt="Download on App Store"
              />
            </AppStoreLink>
            <AppStoreLink
              href="https://play.google.com/store/apps/details?id=app.flinq.athlete"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img
                src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg"
                alt="Get it on Google Play"
              />
            </AppStoreLink>
          </AppStoreLinks>
        </AppBannerContent>
        <AppBannerQRContainer>
          <QRCodeCanvas value={appDeepLink} size={100} />
        </AppBannerQRContainer>
      </AppBanner>

      {!challenge.ended && (
        <>
          {challengeType === 'individual' && !myParticipation && (
            <ActionCard>
              <ActionContent>
                <ActionTitle>
                  {t('challenge.action.joinTitle', { defaultValue: 'Ready to take on this challenge?' })}
                </ActionTitle>
                <ActionDescription>
                  {t('challenge.action.joinDescription', {
                    defaultValue: 'Join now to track your progress and compete on the leaderboard!',
                  })}
                </ActionDescription>
              </ActionContent>
              <ActionButtons>
                <Button color="primary" onClick={handleJoinIndividual} disabled={joinMutation.isPending}>
                  {joinMutation.isPending
                    ? t('challenge.joining', { defaultValue: 'Joining...' })
                    : t('challenge.join', { defaultValue: 'Join Challenge' })}
                </Button>
              </ActionButtons>
            </ActionCard>
          )}

          {challengeType === 'individual' && myParticipation && (
            <ActionCard>
              <ActionContent>
                <ActionTitle>
                  {t('challenge.action.participatingTitle', { defaultValue: "You're participating in this challenge" })}
                </ActionTitle>
                <ActionDescription>
                  {t('challenge.action.participatingDescription', {
                    defaultValue: 'Keep going! Your progress is being tracked.',
                  })}
                </ActionDescription>
              </ActionContent>
              <ActionButtons>
                <Button color="danger" onClick={handleLeaveIndividual} disabled={leaveMutation.isPending}>
                  {leaveMutation.isPending
                    ? t('challenge.leaving', { defaultValue: 'Leaving...' })
                    : t('challenge.leave', { defaultValue: 'Leave Challenge' })}
                </Button>
              </ActionButtons>
            </ActionCard>
          )}

          {challengeType === 'route' && !myTeam && (
            <ActionCard>
              <ActionContent>
                <ActionTitle>
                  {t('team.action.joinTitle', { defaultValue: 'Join or create a team to participate' })}
                </ActionTitle>
                <ActionDescription>
                  {t('team.action.joinDescription', {
                    defaultValue: 'Team challenges are more fun together! Create your own team or join an existing one.',
                  })}
                </ActionDescription>
              </ActionContent>
              <ActionButtons>
                <Button color="primary" onClick={handleCreateTeam}>
                  {t('team.create.button', { defaultValue: 'Create Team' })}
                </Button>
                <Button color="secondary" onClick={handleBrowseTeams}>
                  {t('team.browse.button', { defaultValue: 'Browse Teams' })}
                </Button>
              </ActionButtons>
            </ActionCard>
          )}

          {challengeType === 'route' && myTeam && (
            <ActionCard>
              <ActionContent>
                <ActionTitle>
                  {t('team.action.inTeamTitle', { defaultValue: "You're part of {teamName}", teamName: myTeam.name })}
                </ActionTitle>
                <ActionDescription>
                  {t('team.action.inTeamDescription', {
                    defaultValue: 'Keep going with your team! Your activities contribute to the team score.',
                  })}
                </ActionDescription>
              </ActionContent>
              <ActionButtons>
                <Button color="danger" onClick={handleLeaveTeam} disabled={isLeavingTeam}>
                  {isLeavingTeam
                    ? t('team.leaving', { defaultValue: 'Leaving...' })
                    : t('team.leave.button', { defaultValue: 'Leave Team' })}
                </Button>
              </ActionButtons>
            </ActionCard>
          )}
        </>
      )}

      <ContentRow>
        <MainContent xs={12} lg={8}>
          <ChallengeStats
            challenge={challenge}
            challengeType={challengeType!}
            teamsCount={teams.length}
            participantsCount={challengeType === 'individual' ? (individualChallenge?.totalParticipants ?? 0) : undefined}
          />

          {(myTeam || myParticipation) && (
            <ChallengeMyProgress
              challengeType={challengeType!}
              challenge={challenge}
              myTeam={myTeam ?? null}
              myParticipation={myParticipation ?? null}
              locations={locations}
            />
          )}

          <ChallengeMap
            challenge={challenge}
            locations={locations}
            reachedLocations={
              challengeType === 'individual'
                ? myParticipation?.reachedLocations
                : myTeam?.reachedLocations
            }
            language={language}
            onLocationClick={handleLocationClick}
            myTeamPosition={
              myTeam?.currentPosition?.xorLat
                ? { lat: myTeam.currentPosition.xorLat, lng: myTeam.currentPosition.yorLng }
                : undefined
            }
            myParticipantPosition={
              myParticipation?.currentPosition?.xorLat
                ? { lat: myParticipation.currentPosition.xorLat, lng: myParticipation.currentPosition.yorLng }
                : undefined
            }
            teams={teams
              .filter((t) => t.id !== myTeam?.id && t.currentPosition?.xorLat)
              .map((t) => ({
                id: t.id,
                name: t.name,
                position: { lat: t.currentPosition.xorLat, lng: t.currentPosition.yorLng },
                points: t.totalCollectedPoints,
              }))}
            participants={participants
              .filter((p) => p.id !== myParticipation?.id && p.currentPosition?.xorLat)
              .map((p) => ({
                id: p.id,
                name: p.nickname || 'Participant',
                position: { lat: p.currentPosition.xorLat, lng: p.currentPosition.yorLng },
                points: p.totalCollectedPoints,
              }))}
          />

          <ChallengeLocations
            locations={locations}
            language={language}
            reachedLocations={
              challengeType === 'individual'
                ? myParticipation?.reachedLocations
                : myTeam?.reachedLocations
            }
            onLocationClick={handleLocationClick}
            challengeStarted={challenge.started}
          />
        </MainContent>

        <SidebarContent xs={12} lg={4}>
          <ChallengeLeaderboard
            challenge={challenge}
            challengeType={challengeType!}
            teams={teams}
            participants={participants}
            myTeam={myTeam ?? null}
            myParticipation={myParticipation ?? null}
          />
        </SidebarContent>
      </ContentRow>

      <ChallengeLocationModal
        isOpen={locationModalOpen}
        onClose={handleCloseLocationModal}
        location={selectedLocation}
        locationIndex={selectedLocationIndex}
        totalLocations={locations.length}
        language={language}
        challengeType={challengeType!}
        reachedLocations={
          challengeType === 'individual'
            ? myParticipation?.reachedLocations
            : myTeam?.reachedLocations
        }
        myTotalPoints={
          challengeType === 'individual'
            ? myParticipation?.totalCollectedPoints
            : myTeam?.totalCollectedPoints
        }
        hasJoined={!!(myTeam || myParticipation)}
        challengeStarted={challenge.started}
      />

      {challengeType === 'route' && challengeId && (
        <>
          <CreateTeamModal
            isOpen={createTeamModalOpen}
            onClose={() => setCreateTeamModalOpen(false)}
            challengeId={challengeId}
            onTeamCreated={handleTeamCreated}
          />
          <TeamBrowseModal
            isOpen={browseTeamsModalOpen}
            onClose={() => setBrowseTeamsModalOpen(false)}
            challengeId={challengeId}
            onTeamJoined={handleTeamJoined}
          />
        </>
      )}
    </PageContainer>
  );
});
